import { RefObject, useEffect, useRef } from 'react';

import { trackEvent } from '~/features/analytics/track-event';

import type { ExperienceAtfPromoVariant } from '../constants';
import { formatAtfPromoEventLabel } from '../utils/format-atf-promo-event-label';

/**
 * Fires a single "view" analytics event when the card enters the viewport.
 * `dedupeViewIdsRef` dedupes by `cardId` across Swiper loop duplicates.
 * Event `label` is `formatAtfPromoEventLabel(promoVariant)` so it matches click tracking on the same cards.
 */
export function useCardViewTracking(
  ref: RefObject<HTMLElement | null>,
  meta: { cardId: string; cardPosition: number; promoVariant: ExperienceAtfPromoVariant },
  dedupeViewIdsRef: RefObject<Set<string>>
) {
  const hasFiredThisMount = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || hasFiredThisMount.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasFiredThisMount.current) return;
        const set = dedupeViewIdsRef.current;
        if (!set) return;
        if (set.has(meta.cardId)) {
          hasFiredThisMount.current = true;
          observer.disconnect();
          return;
        }

        hasFiredThisMount.current = true;
        set.add(meta.cardId);
        trackEvent({
          type: 'view',
          name: 'Banner Viewed',
          label: formatAtfPromoEventLabel(meta.promoVariant),
          additionalAttributes: {
            card_position: meta.cardPosition,
          },
        });
        observer.disconnect();
      },
      { threshold: 0.5 }
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, [dedupeViewIdsRef, meta.cardId, meta.cardPosition, meta.promoVariant, ref]);
}
